import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { cloneDeep, uniqBy, property, groupBy } from 'lodash-es'
import { Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SegmentsService {
  private apiEndPoint = '/dashboard/segments';
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  constructor(private http: HttpClient) { }

  private extractData(res: Response) {
    const body = res;
    return body || {};
  }

  upsertSegment(params: any) {
    const endPoint = this.apiEndPoint + '/v1/seg';
    return this.http.post<any>(endPoint, params, this.httpOptions).pipe(
      map(this.extractData)
    );
  }

  /**
   * Update or Create a new segment.
   * Parameter segmentId is optional, API will try to create if segmentId is missing.
   *  */
  upsertSegmentWithParams(appId: string, name: string, desc: string, criteria: any, isDIL: boolean, segmentId?: any): Observable<any> {
    const params = {
      name: name,
      app_id: appId,
      desc: desc,
      criteria: criteria,
      dil: isDIL
    };
    if (segmentId) {
      params['segment_id'] = segmentId;
    }
    return this.upsertSegment(params);
  }

  duplicateSegment(appId: string, segId: string, name: string, desc: string) {
    const endPoint = this.apiEndPoint + '/v1/seg/duplicate';
    const params = {
      app_id: appId,
      segment_id: segId,
      new_segment_name: name,
      desc: desc
    };
    return this.http.post<any>(endPoint, params, this.httpOptions).pipe(
      map(this.extractData)
    );
  }

  upsertNewSubsegments(appId: string, segmentId: string, criteria: any, isDIL: boolean) {
    const params = {
      app_id: appId,
      criteria: criteria,
      dil: isDIL,
      segment_id: segmentId
    };
    return this.upsertSegment(params);
  }

  /**
   * Get segment details of a list of segmentIds
   *
   * @param   string          appId       application Id
   * @param   Array[string]   segmentIds  list of segmentIds
   *
   */
  getSegmentsById(appId, segmentIds): Observable<any> {
    const dataParams = {
      'aid': appId,
      'ids': segmentIds
    };
    return this.getSegments(dataParams);
  }

  /**
   * Fetch list of segments for selected appId
   *
   * @param   string  appId   application Id
   * @param   number  offset  page offset
   * @param   number  limit   page item count
   *
   */
  getSegmentsList(appId, offset, limit): Observable<any> {
    const dataParams = {
      'aid': appId,
      'o': offset,
      'l': limit
    };
    return this.getSegments(dataParams);
  }

  getSegments(params) {
    const endPoint = this.apiEndPoint + '/v1/seg';
    const dataToSend = { 'params': params };
    return this.http.get<any>(endPoint, { ...this.httpOptions, ...dataToSend }).pipe(
      map(this.extractData)
    );
  }

  /**
   * Query segments
   */
  searchSegmentsList(query, appId, offset, limit) {
    const endPoint = this.apiEndPoint + '/v1/search';
    const dataParams = {
      'aid': appId,
      'q': query,
      'o': offset,
      'l': limit
    };
    const dataToSend = { 'params': dataParams };
    return this.http.get<any>(endPoint, { ...this.httpOptions, ...dataToSend }).pipe(
      map(this.extractData)
    );
  }

  /**
   * Delete selected segment by segment Id
   *
   * @param   string   appId       application Id
   * @param   string   segmentIds  segmentId to delete
   */
  deleteSegment(appId, segmentId): Observable<any> {
    const endPoint = this.apiEndPoint + '/v1/del';
    const dataToSend = {
      'params': {
        'aid': appId,
        'ids': [segmentId]
      }
    };
    return this.http.get<any>(endPoint, { ...this.httpOptions, ...dataToSend }).pipe(
      map(this.extractData)
    );
  }

  getDilSegmentProgress(appId) {
    const endPoint = '/dashboard/ujmanalytics/dil/segment/status/' + appId;
    return this.http.get<any>(endPoint, { ...this.httpOptions }).pipe(
      map(this.extractData)
    );
  }

  /**
   * Search events for current app Id with matching Name or vendor
   *
   */
  getEvents(appId, offset, limit, vendor?, ipa_only?, q?) {
    let url = '/dashboard/event/v1/' + appId + '?o=' + offset + '&l=' + limit + '&t=0';
    if (!offset && !limit) {
      url = '/dashboard/event/v1/' + appId + '?t=0';
    }
    url += (q && q !== '') ? '&q=' + q : '';
    url += vendor ? '&v=' + vendor : '';
    url += ipa_only ? '&ipa_only=true' : '';
    return this.http.get<any>(url).pipe(
      map((res: Response) => {
        const body = res;
        return body || {};
      })
    );
  }

  getSMTEventNameMap() {
    const url = `https://cdnt.netcoresmartech.com/system_activity.json`;
    return this.http.get<any>(url).pipe(
      map((res: Response) => {
        const body = res;
        return body || {};
      })
    );
  }

  /**
   * update IPA events for current app Id with matching Name or vendor
   *
   * payload:
      {
        "attach_evt_list": [
            “5c94b0200e680b9137a6bb94”
          ],
        “detach_evt_list”: [
            “5c94b9137a6bb940200e680b”
          ]
      }
   */
  setIPAEvents(appId, vendor, payload) {
    const url = '/dashboard/event/v1/' + appId + '/ipa';
    if (vendor) {
      payload['v'] = vendor;
    }
    return this.http.post<any>(url, payload, this.httpOptions).pipe(
      map(this.extractData)
    );
  }


  /**
   * Search segments for current app Id for matching Name or desc
   *
   * @param   string  appId   application id
   * @param   string  query   query string to search for
   * @param   number  offset  page offset
   * @param   number  limit   page size
   *
   */
  searchSegments(appId, query, offset, limit) {
    const endPoint = this.apiEndPoint + '/v1/search';
    const dataToSend = {
      'params': {
        'aid': appId,
        'q': query,
        'o': offset,
        'l': limit
      }
    };
    return this.http.get<any>(endPoint, { ...this.httpOptions, ...dataToSend }).pipe(
      map(this.extractData)
    );
  }

  categorizeEvents(events, hideEventCategory = {}) {
    const eventCategoryMap = events.reduce((acc, event) => {
      if (hideEventCategory.hasOwnProperty(event.ven)) {
        return acc;
      }
      let vendor = event.ven;
      if (!event.ven) {
        vendor = 'misc';
      }
      if (!acc.hasOwnProperty(vendor)) {
        acc[vendor] = [];
      }
      acc[vendor].push(event);
      return acc;
    }, {});

    Object.keys(eventCategoryMap).forEach(vendor => {
      eventCategoryMap[vendor] = uniqBy(eventCategoryMap[vendor], 'name');
    });
    return eventCategoryMap;
  }

  getCategoriesMap() {
    return {
      mxp: 'Mixpanel',
      ctp: 'Clevertap',
      amp: 'Amplitude',
      ga: 'Google Analytics',
      smt: 'Smartech Events',
      fbs: 'Firebase',
      omni: 'Omniture',
      loca: 'Localytics',
      wbe: 'Webengage',
      hsl: 'Hansel Events',
      sgmt: 'Segment',
      dthw: 'Data Highway',
      apfr: 'AppsFlyer',
      adobe: 'Adobe'
    };
  }

  updateAttachEventListForSegments(appId, segmentList, data) {
    if (data[0].is_error || data[1].is_error) {
      return;
    }
    const ipaEvents = data[0].api_response.events.reduce((acc, cur) => {
      acc[cur.id] = cur;
      return acc;
    }, {});
    let promiseArr = [];
    segmentList.forEach(segment => {
      const completeEventList = data[1].api_response.events;
      const finalAttachedEvents = this.getAttachedEvents(segment, completeEventList);
      const remainingEvents = [];
      const finalEventsId = Object.keys(finalAttachedEvents);

      finalEventsId.forEach(eventId => {
        if (ipaEvents.hasOwnProperty(eventId)) {
          return;
        }
        remainingEvents.push(finalAttachedEvents[eventId]);
      });

      if (!remainingEvents.length) {
        return;
      }
      promiseArr = promiseArr.concat(this.getSetIPAPromises(appId, remainingEvents));
    });
    return forkJoin(promiseArr)
      .subscribe((ipaSetResponse: any) => {
        // console.log('data!', ipaSetResponse);
      });
  }

  getSetIPAPromises(appId, events) {
    let attach_evt_list: any = events;
    const detach_evt_list: any = [];

    attach_evt_list = groupBy(attach_evt_list, (ev: any) => {
      return ev.pi;
    });

    const ipaPromises = [];
    Object.keys(attach_evt_list).forEach(ven => {
      const promise = this.setIPAEvents(appId,
        ven, {
          'attach_evt_list': (attach_evt_list[ven] || []).map(property('event_id')),
          'detach_evt_list': (detach_evt_list[ven] || []).map(property('event_id'))
        });
      ipaPromises.push(promise);
    });
    return ipaPromises;
  }

  getAttachedEvents(segmentData, completeEventList) {
    const eventList = {};
    if (!(segmentData && segmentData.criteria && segmentData.criteria.rules)) {
      return;
    }
    segmentData.criteria.rules.forEach(rule => {
      if (rule.hasOwnProperty('n') && rule['n'] !== 'hansel_nudge_event' && rule['n'] !== '_hsl_page_load') {
        const eventObject = this.getEventIdForRule(rule, completeEventList);
        if (!eventObject) {
          return;
        }
        const ruleClone = cloneDeep(rule);
        ruleClone.event_id = eventObject.id;
        eventList[eventObject.id] = ruleClone;
      }
    });
    return eventList;
  }

  getEventIdForRule(rule, eventList) {
    return eventList.filter(event => event.ven === rule.pi && event.name === rule.n)[0];
  }

  fetchAllEvents(appId, offset, limit, vendor?, ipa_only?) {
    const eventList = [];
    return new Observable(observer => {
      const fetchEvents = (o) => {
        this.getEvents(appId, o, limit, vendor, ipa_only).subscribe((res: any) => {
          if (!res['is_error']) {
            eventList.push(...res['api_response']['events']);
            const tc = res['api_response']['total_count'];
            if (tc > eventList.length) {
              return fetchEvents(eventList.length);
            }
          }
          res['api_response']['events'] = eventList;
          observer.next(res);
          observer.complete();
        });
      }
      fetchEvents(offset);
    });
  }
}
